// Copyright (c) 2021 WiseTime. All rights reserved.

import React, { useEffect, useMemo, memo } from "react"
import { useDispatch, useSelector } from "react-redux"
import styled from "styled-components/macro"
import DateTab from "./DateTab"
import * as TimelineSlice from "../slices/timeline"
import { getDatesOfWeek, toDate, TimelineDate } from "features/timeline/dateTime"
import { DateTabsWrapper } from "../../shared/styles"
import * as ToolbarSlice from "../toolbar/slice"
import * as ToolbarActions from "../toolbar/actions"
import { isSearchNotEmpty } from "../search-bar/searchUtils"

const StyledDateTabsWrapper = styled(DateTabsWrapper)`
  @media (max-width: ${({ theme }): string => theme.screen.sm}) and (orientation: landscape) {
    border-bottom: 1px solid ${({ theme }): string => theme.colors.silverSand};
  }
`

const DateTabs = (): React.ReactElement | null => {
  const dispatch = useDispatch()
  const activeDate = useSelector(TimelineSlice.selectActiveDate)
  const timeZoneOffset = useSelector(TimelineSlice.selectTimeZone)?.offset
  const showMatchedLabel = useSelector(ToolbarSlice.selectSearchTerms).length > 0
  const datesOfWeek = useMemo(
    () => getDatesOfWeek(toDate(activeDate)),
    [activeDate],
  )

  const searchTermsValues = useSelector(ToolbarSlice.selectSearchTermsValues)
  useEffect(() => {
    if (timeZoneOffset !== undefined || !isSearchNotEmpty(searchTermsValues)) {
      return
    }
    dispatch(ToolbarActions.countFilteredTimeRowsByDay())

    // resend request only if week or timeZoneOffset is changed
    // sending request on filter change is handled in countFilteredTimeRowsByDay epic
  }, [timeZoneOffset])

  return (
    <StyledDateTabsWrapper>
      {datesOfWeek.map((date: TimelineDate) => (
        <DateTab
          key={date}
          // eslint-disable-next-line lingui/no-unlocalized-strings
          dateLabel={toDate(date).setLocale(navigator.language).toFormat("EEE MMM d").toUpperCase()}
          timelineDate={date}
          isActive={date === activeDate}
          showMatchedLabel={showMatchedLabel}
        />
      ))}
    </StyledDateTabsWrapper>
  )
}

export default memo(DateTabs)
